import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../store/auth.js';
import { apiGet, apiPut, apiPost } from '../../lib/api.js';
import { initials } from '../../lib/format.js';
import { Card, Field, SaveButton, isDirty } from './_shared.jsx';
import GoogleCalendarCard from './GoogleCalendarCard.jsx';
import LicensedStatesPicker from '../../components/LicensedStatesPicker.jsx';
import { MapPin } from 'lucide-react';

const TZ = ['America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Chicago', 'America/New_York', 'America/Anchorage', 'Pacific/Honolulu'];

export default function ProfileTab() {
  const { agent, token, setSession } = useAuth();
  const [loaded, setLoaded] = useState(null);   // server snapshot
  const [draft, setDraft] = useState(null);     // user-edited copy
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });

  useEffect(() => {
    apiGet('/settings/profile').then((r) => {
      if (r?.ok) {
        setLoaded(r.profile);
        setDraft(r.profile);
      }
    }).catch(() => toast.error('Failed to load profile'));
  }, []);

  async function save() {
    if (!draft) return;
    const prev = loaded;
    setBusy(true);
    setLoaded(draft); // optimistic
    try {
      const r = await apiPut('/settings/profile', {
        name: draft.name,
        timezone: draft.timezone,
        // Self-serve licensed-states edit — always send the current array so
        // agents can also *clear* states they no longer hold. Server
        // allowlists this field.
        licensedStates: Array.isArray(draft.licensedStates) ? draft.licensedStates : [],
        // PR #408 — text-bot voice fields. Server allowlists these; only
        // send when set so we don't clobber the role-based default with an
        // empty string on first save.
        ...(draft.title !== undefined && draft.title !== null ? { title: draft.title } : {}),
        ...(draft.pronoun ? { pronoun: draft.pronoun } : {}),
      });
      if (!r?.ok) throw new Error('Save failed');
      setLoaded(r.profile);
      setDraft(r.profile);
      // Refresh top-level auth so sidebar/avatar pick up the new name.
      setSession(token, { ...agent, ...r.profile });
      toast.success('Profile saved');
    } catch (e) {
      setLoaded(prev);
      setDraft(prev);
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function changePassword() {
    if (!pw.current || !pw.next) { toast.error('Fill in current and new password'); return; }
    if (pw.next !== pw.confirm) { toast.error('Passwords do not match'); return; }
    if (pw.next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    setPwBusy(true);
    try {
      // Reuses the existing change-password endpoint on /api/auth.
      await apiPost('/settings/profile/change-password', { currentPassword: pw.current, newPassword: pw.next });
      toast.success('Password updated');
      setPw({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Failed to change password');
    } finally {
      setPwBusy(false);
    }
  }

  if (!draft) return <Card title="Profile">Loading…</Card>;
  const dirty = isDirty(draft, loaded);

  return (
    <div className="space-y-4">
      <Card
        title="Your Profile"
        description="Name, email, timezone, and avatar shown across CIVIX."
        actions={<SaveButton onClick={save} dirty={dirty} busy={busy} />}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold text-white shrink-0"
            style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #E040FB 60%, #3B82F6 100%)' }}
          >
            {agent?.avatar
              ? <img src={agent.avatar} alt={draft.name} className="w-full h-full rounded-full object-cover"/>
              : initials(draft.name)}
          </div>
          <div className="text-xs text-white/50">
            Avatar uploads use the existing endpoint. Use the legacy <em>Profile Photo</em> tool to change it.
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Full name">
            <input
              className="input"
              value={draft.name || ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </Field>
          <Field label="Email" hint="Email changes happen via support — contact ops to update.">
            <input className="input opacity-60" value={draft.email || ''} disabled />
          </Field>
          <Field label="Timezone">
            <select
              className="input"
              value={draft.timezone || 'America/Denver'}
              onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
            >
              {TZ.map((z) => <option key={z}>{z}</option>)}
            </select>
          </Field>
          <Field label="Role" hint="Roles are managed by your admin.">
            <input className="input opacity-60 capitalize" value={draft.role || ''} disabled />
          </Field>
        </div>
      </Card>

      {/* Licensed States — self-serve editor. Agents were told at signup they
          could update this in Settings later; this card fulfils that promise
          and is what the amber Dashboard banner links to via /settings. */}
      <Card
        title="Licensed States"
        description="Update the states where you currently hold an active insurance license. Used by lead routing — leads will only be assigned to you in states you're licensed in."
        actions={<SaveButton onClick={save} dirty={dirty} busy={busy} />}
      >
        <div className="space-y-2">
          <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-white/50 font-medium">
            <MapPin className="w-3 h-3" /> Your Licensed States
          </div>
          <LicensedStatesPicker
            value={draft.licensedStates || []}
            onChange={(next) => setDraft({ ...draft, licensedStates: next })}
          />
          <div className="text-[11px] text-white/40">
            Changes take effect immediately for lead routing. Keep this current — leads assigned in states you're no longer licensed in create compliance risk.
          </div>
        </div>
      </Card>

      {/* PR #408 — Text-bot voice: title + gender */}
      <Card
        title="Text-Bot Voice"
        description={'Controls how our AI text bots refer to you. Example: "Easton is our head underwriter — he’s in charge of your underwriting checks…"'}
        actions={<SaveButton onClick={save} dirty={dirty} busy={busy} />}
      >
        <div className="grid md:grid-cols-2 gap-4">
          <Field
            label="Title"
            hint="Rendered inline in bot copy. Defaults to 'head underwriter' for admins, 'licensed agent' otherwise."
          >
            <input
              className="input"
              data-testid="agent-title-input"
              value={draft.title || ''}
              maxLength={60}
              placeholder="head underwriter"
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </Field>
          <Field
            label="Gender"
            hint="Used to pick 'he’s' or 'she’s' in bot copy."
          >
            <select
              className="input"
              data-testid="agent-pronoun-select"
              value={draft.pronoun || 'he'}
              onChange={(e) => setDraft({ ...draft, pronoun: e.target.value })}
            >
              <option value="he">Male (“he’s”)</option>
              <option value="she">Female (“she’s”)</option>
            </select>
          </Field>
        </div>
      </Card>


      <GoogleCalendarCard />

      <Card title="Change Password" description="Use a strong password — at least 8 characters.">
        <div className="grid md:grid-cols-3 gap-3 max-w-3xl">
          <input className="input" type="password" placeholder="Current password"
            value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}/>
          <input className="input" type="password" placeholder="New password"
            value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}/>
          <input className="input" type="password" placeholder="Confirm new password"
            value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}/>
        </div>
        <button
          className="btn-primary disabled:opacity-40"
          onClick={changePassword}
          disabled={pwBusy || !pw.current || !pw.next}
        >
          {pwBusy ? 'Updating…' : 'Update Password'}
        </button>
      </Card>
    </div>
  );
}
